import { Component, Input, forwardRef, Output, EventEmitter, OnInit } from '@angular/core';
import { FormControl, AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AddressProvider } from '@internal-providers/address.provider';
import { getAddressString } from '@shared-libs/helpers';
import { IAddress } from '@shared-models/address.model';

@Component({
	selector: 'form-search-address',
	templateUrl: 'form-search-address.component.html',
	styleUrls: ['./form-search-address.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FormSearchAddressComponent),
			multi: true,
		},
	],
})
export class FormSearchAddressComponent implements ControlValueAccessor, OnInit {
	@Input() public label: string;
	@Input() public readonly: boolean = false;
	@Input() public formControl: FormControl;
	@Output() public readonly selected: EventEmitter<IAddress> = new EventEmitter();
	public controlName: string;
	public value: string | IAddress;
	public searchResults: Array<{ description: string; id: string }> = new Array();
	public inputId: number;
	public loading: boolean = false;
	public error: boolean = false;

	public disabled: boolean;
	public onChange: (_: any) => void;
	public onTouched: () => void;

	constructor(private readonly provider: AddressProvider) {
		this.addResetSearchEventListeners();
		this.onChange = (_: any) => {};
		this.onTouched = () => {};
		this.disabled = false;
	}

	public inputChanged(value: string | IAddress): void {
		this.value = value;
		this.onChange(this.value);
		this.onTouched();
	}

	public ngOnInit(): void {
		if (this.formControl) {
			this.controlName = this.getControlName(this.formControl);
		}
		this.inputId = Math.random();
	}

	public autocomplete(searchString: string): void {
		if (searchString.length > 2) {
			this.loading = true;
			this.provider
				.autocomplete(searchString)
				.then((data) => {
					this.loading = false;
					this.searchResults = data;
				})
				.catch(() => {
					this.loading = false;
					this.error = true;
				});
		} else this.resetSearch();
	}

	public async selectAddress(_address: { description: string; id: string }): Promise<void> {
		this.resetSearch();
		(document.getElementById(`address-input-${this.inputId}`) as HTMLInputElement).value = this.formControl
			? _address.description
			: '';
		const address = await this.provider.getAutocompleteAddressById(_address.id);
		if (this.formControl) {
			(document.getElementById(`address-input-${this.inputId}`) as HTMLInputElement).value =
				getAddressString(address);
			this.inputChanged(address);
		} else {
			this.selected.emit(address);
		}
	}

	public resetSearch(): void {
		this.searchResults = new Array();
	}

	public addResetSearchEventListeners(): void {
		document.addEventListener('keydown', (event: KeyboardEvent) => {
			if (
				event.key === 'Escape' ||
				event.key === 'Esc' ||
				event.keyCode === 27 ||
				event.key === 'Tab' ||
				event.keyCode === 9
			) {
				this.resetSearch();
			}
		});
		document.addEventListener('click', (event) => {
			if ((event.target as HTMLElement).className !== '_search-result') {
				this.resetSearch();
			}
		});
	}

	public getControlName(c: AbstractControl): string | null {
		const formGroup = c.parent.controls;
		return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
	}

	public writeValue(value: string | IAddress): void {
		this.value = value;
	}
	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}
	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}
	public setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}
